import React from 'react';

/**
 * Template 2: Annotated Face
 * Close-up face centered/right, 4-8 numbered labels scattered around
 * with connecting arrows. Headline banner at top.
 *
 * Inspired by: "8 FIGURE TRAITS", "Dial This In & Scale"
 */
export default function JH_AnnotatedFace({
  headline = '8 FIGURE TRAITS',
  personImage,
  bgColor = '#2D3748',
  accentColor = '#00BFA5',
  labels = ['Urgency Activation', 'Intolerable Standards', 'Revenue-First Thinking', 'Relentless Iteration', 'Accountability Pressure', 'Anti-Comfort Bias'],
}) {
  const isLightBg = isLightColor(bgColor);
  const textColor = isLightBg ? '#111' : '#fff';

  // Position labels around the face in an arc pattern
  const leftPositions = [
    { top: 180, left: 40 },
    { top: 290, left: 30 },
    { top: 400, left: 40 },
    { top: 510, left: 50 },
  ];
  const rightPositions = [
    { top: 180, left: 720 },
    { top: 290, left: 740 },
    { top: 400, left: 720 },
    { top: 510, left: 710 },
  ];

  return (
    <div style={{
      width: 1280, height: 720, position: 'relative', overflow: 'hidden',
      background: bgColor, fontFamily: "'Inter', sans-serif",
    }}>
      {/* Headline banner at top */}
      <div style={{
        position: 'absolute', top: 30, left: 0, right: 0, zIndex: 3,
        textAlign: 'center',
      }}>
        <span style={{
          display: 'inline-block', background: accentColor,
          padding: '8px 32px', fontSize: 64, fontWeight: 900,
          color: isLightColor(accentColor) ? '#111' : '#fff',
          textTransform: 'uppercase', letterSpacing: '-1px',
        }}>
          {headline}
        </span>
      </div>

      {/* Person image (centered-right) */}
      {personImage ? (
        <div style={{
          position: 'absolute', right: 160, bottom: 0, width: 550, height: 600,
          zIndex: 1,
        }}>
          <img src={personImage} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} />
        </div>
      ) : (
        <div style={{
          position: 'absolute', right: 200, bottom: 0, width: 480, height: 560,
          background: 'rgba(255,255,255,0.06)', borderRadius: '12px 12px 0 0',
          zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 36, color: 'rgba(255,255,255,0.15)',
        }}>PHOTO</div>
      )}

      {/* Labels - Left side */}
      {labels.slice(0, Math.ceil(labels.length / 2)).map((label, i) => {
        const pos = leftPositions[i] || leftPositions[leftPositions.length - 1];
        return (
          <div key={`l-${i}`} style={{
            position: 'absolute', top: pos.top, left: pos.left, zIndex: 2,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: '50%',
              background: accentColor, color: isLightColor(accentColor) ? '#111' : '#fff',
              fontSize: 20, fontWeight: 800,
            }}>{i + 1}</span>
            <span style={{
              fontSize: 22, fontWeight: 700, color: textColor,
              textShadow: isLightBg ? 'none' : '0 1px 4px rgba(0,0,0,0.5)',
              maxWidth: 260,
            }}>{label}</span>
          </div>
        );
      })}

      {/* Labels - Right side */}
      {labels.slice(Math.ceil(labels.length / 2)).map((label, i) => {
        const pos = rightPositions[i] || rightPositions[rightPositions.length - 1];
        const num = Math.ceil(labels.length / 2) + i + 1;
        return (
          <div key={`r-${i}`} style={{
            position: 'absolute', top: pos.top, left: pos.left, zIndex: 2,
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 36, height: 36, borderRadius: '50%',
              background: accentColor, color: isLightColor(accentColor) ? '#111' : '#fff',
              fontSize: 20, fontWeight: 800,
            }}>{num}</span>
            <span style={{
              fontSize: 22, fontWeight: 700, color: textColor,
              textShadow: isLightBg ? 'none' : '0 1px 4px rgba(0,0,0,0.5)',
              maxWidth: 260,
            }}>{label}</span>
          </div>
        );
      })}
    </div>
  );
}

function isLightColor(color) {
  const hex = (color || '#000').replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;
  const toL = c => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * toL(r) + 0.7152 * toL(g) + 0.0722 * toL(b) > 0.35;
}
